import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import type { DowntimeCause, DowntimeType, SlaDowntimeLog } from '@/api/services';
import { useCreateDowntimeLog, useUpdateDowntimeLog, type DowntimeLogPayload } from '@/api/services-mutations';
import {
  DOWNTIME_TYPE_OPTIONS,
  DOWNTIME_CAUSE_OPTIONS,
  DOWNTIME_CAUSE_COUNTS_SLA,
} from './sla-constants';

function errMsg(err: unknown): string {
  const r = (err as { response?: { status?: number; data?: { error?: string; error_description?: string; conflicting_id?: number; message?: string } } })?.response;
  if (r?.status === 409 && r.data?.error === 'downtime-overlap-detected') {
    return `This time range overlaps log #${r.data.conflicting_id ?? '?'}. Edit the existing log instead of creating a new one.`;
  }
  return r?.data?.error_description ?? r?.data?.message ?? r?.data?.error ?? 'Something went wrong.';
}

/** ISO string → `YYYY-MM-DDThh:mm` for a datetime-local input (local tz). */
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const nowLocal = () => toLocalInput(new Date().toISOString());

/**
 * Record or edit a downtime incident (`POST|PUT /services/:id/sla/downtime`).
 * The "counts against SLA" toggle auto-follows the selected cause but can be
 * overridden; an exclusion reason is required when it is turned off.
 */
export function SlaDowntimeDialog({
  serviceId,
  editing,
  open,
  onOpenChange,
}: {
  serviceId: number;
  editing?: SlaDowntimeLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const create = useCreateDowntimeLog(serviceId);
  const update = useUpdateDowntimeLog(serviceId);
  const pending = create.isPending || update.isPending;

  const [type, setType] = useState<DowntimeType>('unplanned');
  const [cause, setCause] = useState<DowntimeCause>('provider');
  const [start, setStart] = useState(nowLocal());
  const [end, setEnd] = useState('');
  const [description, setDescription] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [counts, setCounts] = useState(true);
  const [excludedReason, setExcludedReason] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setType(editing.downtime_type);
      setCause(editing.downtime_cause ?? 'provider');
      setStart(toLocalInput(editing.downtime_start));
      setEnd(toLocalInput(editing.downtime_end));
      setDescription(editing.downtime_description ?? '');
      setRootCause(editing.root_cause ?? '');
      setCounts(editing.counts_against_sla);
      setExcludedReason(editing.excluded_reason ?? '');
    } else {
      setType('unplanned'); setCause('provider'); setStart(nowLocal()); setEnd('');
      setDescription(''); setRootCause(''); setCounts(true); setExcludedReason('');
    }
  }, [open, editing]);

  // Auto-set "counts against SLA" from the cause (mirrors legacy behaviour).
  useEffect(() => { setCounts(DOWNTIME_CAUSE_COUNTS_SLA[cause]); }, [cause]);

  if (!open) return null;

  const submit = () => {
    if (!start) { toast.error('Please choose a start time'); return; }
    const payload: DowntimeLogPayload = {
      downtime_type: type,
      downtime_cause: cause,
      downtime_start: new Date(start).toISOString(),
      downtime_end: end ? new Date(end).toISOString() : undefined,
      downtime_description: description || undefined,
      root_cause: rootCause || undefined,
      counts_against_sla: counts,
      excluded_reason: !counts ? excludedReason || undefined : undefined,
    };
    const onDone = { onSuccess: () => { toast.success(editing ? 'Downtime updated' : 'Downtime logged'); onOpenChange(false); }, onError: (e: unknown) => toast.error(errMsg(e)) };
    if (editing) update.mutate({ downtimeId: editing.downtime_log_id, payload }, onDone);
    else create.mutate(payload, onDone);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit downtime log' : 'Log downtime'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Downtime type">
                {() => (
                  <Select value={type} onValueChange={(v) => setType(v as DowntimeType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DOWNTIME_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Cause">
                {() => (
                  <Select value={cause} onValueChange={(v) => setCause(v as DowntimeCause)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DOWNTIME_CAUSE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Start time" required>
                {(p) => <Input {...p} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />}
              </Field>
              <Field label="End time" description="Leave empty if ongoing">
                {(p) => <Input {...p} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />}
              </Field>
            </div>
            <Field label="Description">
              {(p) => <Textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the incident…" />}
            </Field>
            <Field label="Root cause">
              {(p) => <Textarea {...p} rows={2} value={rootCause} onChange={(e) => setRootCause(e.target.value)} placeholder="Root cause…" />}
            </Field>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--vn-space-3)', borderRadius: 'var(--vn-radius-md)', background: counts ? 'var(--vn-danger-subtle)' : 'var(--vn-success-subtle)' }}>
              <div>
                <div style={{ fontWeight: 'var(--vn-font-weight-semibold)' }}>Counts against SLA</div>
                <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>Auto-set by cause, can be overridden.</div>
              </div>
              <Switch checked={counts} onCheckedChange={setCounts} />
            </div>
            {!counts && (
              <Field label="Exclusion reason">
                {(p) => <Textarea {...p} rows={2} value={excludedReason} onChange={(e) => setExcludedReason(e.target.value)} />}
              </Field>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={pending}>{editing ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
